/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  load locale price data thunk
 */

import { definitelyError, isFailedToFetchError, isPriceDataResourceError } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import { ON_SERVER } from '@zcode/nr/es/shared/env'
import { logger } from '../../logger'
import {
  clearPriceChartData,
  doneFetchingCityPriceChartData,
  doneFetchingHoodPriceChartData,
  setCityPriceChartData,
  setHoodPriceChartData,
  startFetchingCityPriceChartData,
  startFetchingHoodPriceChartData,
} from '../actions'
import {
  selectLocaleCityPriceCurrentKey,
  selectLocaleCityPriceFetchingKey,
  selectLocaleHoodPriceCurrentKey,
  selectLocaleHoodPriceFetchingKey,
} from '../selectors/priceChart'
import type { Dispatch, GetState } from '../types'

export interface LoadLocalePriceData {
  cityState?: string
  hoodPath?: string
}

export const loadLocalePriceData =
  ({ cityState, hoodPath }: LoadLocalePriceData) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const cityCurrentKey = selectLocaleCityPriceCurrentKey(getState())
    const hoodCurrentKey = selectLocaleHoodPriceCurrentKey(getState())
    const hoodKey = hoodPath && cityState ? `${cityState}/${hoodPath}` : null

    if (hoodKey && hoodKey !== hoodCurrentKey) {
      dispatch(startFetchingHoodPriceChartData({ key: hoodKey }))
      return await Api.newPriceData
        .get({ region: hoodKey })
        .then((response) => {
          if (response?.data) {
            const hoodFetchingKey = selectLocaleHoodPriceFetchingKey(getState())
            if (ON_SERVER || hoodKey === hoodFetchingKey) {
              dispatch(
                setHoodPriceChartData({
                  priceChartData: response.data,
                  key: hoodKey,
                })
              )
            }
            return response
          }
        })
        .catch((err: unknown) => {
          if (!isPriceDataResourceError(err) && !isFailedToFetchError(err)) {
            const msg = 'Error in hood loadLocalePriceData'
            logger.error(msg, hoodPath, definitelyError(msg, err))
          }
        })
        .finally(() => {
          dispatch(doneFetchingHoodPriceChartData())
        })
    } else if (!hoodPath && cityState && cityState !== cityCurrentKey) {
      dispatch(clearPriceChartData())
      dispatch(startFetchingCityPriceChartData({ key: cityState }))
      return await Api.newPriceData
        .get({ region: cityState })
        .then((response) => {
          if (response?.data) {
            const cityFetchingKey = selectLocaleCityPriceFetchingKey(getState())
            if (ON_SERVER || cityState === cityFetchingKey) {
              dispatch(
                setCityPriceChartData({
                  priceChartData: response.data,
                  key: cityState,
                })
              )
            }
            return response
          }
        })
        .catch((err: unknown) => {
          if (!isPriceDataResourceError(err) && !isFailedToFetchError(err)) {
            const msg = 'Error in city loadLocalePriceData'
            logger.error(msg, cityState, definitelyError(msg, err))
          }
        })
        .finally(() => {
          dispatch(doneFetchingCityPriceChartData())
        })
    }
  }
